<template>
  <div class="media-source-linker">
    <div class="box">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h4 class="subtitle is-5 mb-0">
              <span class="icon"><i class="mdi mdi-link-variant"></i></span>
              Link Source File
            </h4>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-field>
              <b-select v-model="selectedStorageConfigId" placeholder="Select Storage" size="is-small" @input="onStorageChange">
                <option v-for="config in storageConfigs" :key="config.id" :value="config.id">
                  {{ config.config_name }} ({{ config.bucket_name }})
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>

      <!-- Current linked file info -->
      <div v-if="linkedFile" class="linked-file-info mt-4">
        <div class="notification is-success is-light">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <span class="icon"><i class="mdi mdi-file-check"></i></span>
              </div>
              <div class="level-item">
                <div>
                  <p><strong>{{ linkedFile.filename }}</strong></p>
                  <p class="is-size-7 has-text-grey">
                    {{ linkedFile.storagePath }}
                  </p>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <b-button type="is-danger" size="is-small" icon-left="link-off" @click="unlinkFile">
                  Unlink
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Transcode Actions when file is linked -->
        <div class="qc-actions mt-4">
          <!-- Workflow selector -->
          <div class="columns is-vcentered mb-2" v-if="workflowTemplates.length > 1">
            <div class="column is-narrow">
              <b-field label="Workflow" label-position="on-border">
                <b-select v-model="selectedWorkflowTemplateId" size="is-small">
                  <option v-for="wf in workflowTemplates" :key="wf.id" :value="wf.id">
                    {{ wf.template_name }}
                  </option>
                </b-select>
              </b-field>
            </div>
          </div>
          <div class="buttons">
            <b-button
              v-if="canSubmitForProcessing"
              type="is-success"
              icon-left="cog-sync"
              :loading="submittingJob"
              @click="submitForTranscode"
            >
              Submit for QC & Processing
            </b-button>
          </div>
          <p v-if="canSubmitForProcessing" class="help">
            QC analysis + EBU R128 loudness normalization + encode for playout
          </p>

          <!-- Job Progress Display -->
          <div v-if="activeJob" class="job-progress mt-4">
            <div class="notification" :class="getJobNotificationClass(activeJob.status)">
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <span class="icon">
                      <i :class="getJobStatusIcon(activeJob.status)"></i>
                    </span>
                  </div>
                  <div class="level-item">
                    <div>
                      <p class="has-text-weight-bold">{{ getJobStatusLabel(activeJob.status) }}</p>
                      <p class="is-size-7" v-if="activeJob.statusMessage">{{ activeJob.statusMessage }}</p>
                    </div>
                  </div>
                </div>
                <div class="level-right" v-if="activeJob.progress > 0 && activeJob.progress < 100">
                  <div class="level-item">
                    <span>{{ activeJob.progress }}%</span>
                  </div>
                </div>
              </div>
              <progress
                v-if="activeJob.progress > 0 && activeJob.progress < 100"
                class="progress is-small mt-2"
                :class="getProgressClass(activeJob.status)"
                :value="activeJob.progress"
                max="100"
              >{{ activeJob.progress }}%</progress>
            </div>
          </div>
        </div>
      </div>

      <!-- Browse for file when not linked -->
      <div v-else class="browse-section mt-4">
        <b-button
          type="is-primary"
          icon-left="file-search"
          :disabled="!selectedStorageConfigId"
          @click="openFileBrowser"
          expanded
        >
          Browse for Source File
        </b-button>
        <p class="help mt-2 has-text-centered">
          Select the source file from storage to link with this media
        </p>
      </div>
    </div>

    <!-- QC Status Panel -->
    <div v-if="qcStatus" class="qc-status-panel mt-4">
      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h4 class="subtitle is-6 mb-0">
                <span class="icon"><i class="mdi mdi-check-decagram"></i></span>
                QC Status
              </h4>
            </div>
            <div class="level-item">
              <b-tag :type="getQcStatusType(qcStatus.status)" size="is-medium">
                {{ getQcStatusLabel(qcStatus.status) }}
              </b-tag>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item" v-if="qcStatus.reportUrl">
              <b-button
                type="is-info"
                size="is-small"
                icon-left="file-pdf-box"
                @click="openQcReport"
              >
                View Report
              </b-button>
            </div>
          </div>
        </div>

        <!-- QC Summary Stats -->
        <div class="columns is-multiline mt-3" v-if="qcStatus.results">
          <div class="column is-2">
            <div class="has-text-centered">
              <p class="heading">Errors</p>
              <p class="title is-4" :class="qcStatus.results.errorCount > 0 ? 'has-text-danger' : 'has-text-success'">
                {{ qcStatus.results.errorCount || 0 }}
              </p>
            </div>
          </div>
          <div class="column is-2">
            <div class="has-text-centered">
              <p class="heading">Warnings</p>
              <p class="title is-4" :class="qcStatus.results.warningCount > 0 ? 'has-text-warning' : 'has-text-success'">
                {{ qcStatus.results.warningCount || 0 }}
              </p>
            </div>
          </div>
          <div class="column is-3">
            <div class="has-text-centered">
              <p class="heading">Loudness (LUFS)</p>
              <p class="title is-5" :class="qcStatus.results.loudnessCompliant ? 'has-text-success' : 'has-text-danger'">
                {{ qcStatus.results.integratedLoudness ? qcStatus.results.integratedLoudness.toFixed(1) : 'N/A' }}
              </p>
              <p class="is-size-7 has-text-grey">Target: -23 LUFS</p>
            </div>
          </div>
          <div class="column is-3">
            <div class="has-text-centered">
              <p class="heading">True Peak (dBTP)</p>
              <p class="title is-5" :class="getTruePeakClass(qcStatus.results.truePeak)">
                {{ qcStatus.results.truePeak !== undefined ? qcStatus.results.truePeak.toFixed(1) : 'N/A' }}
              </p>
              <p class="is-size-7 has-text-grey">Max: -3 dBTP</p>
            </div>
          </div>
          <div class="column is-2">
            <div class="has-text-centered">
              <p class="heading">A/V Sync</p>
              <p class="title is-5">
                <span class="icon" :class="qcStatus.results.avSyncOk ? 'has-text-success' : 'has-text-danger'">
                  <i :class="qcStatus.results.avSyncOk ? 'mdi mdi-check-circle' : 'mdi mdi-close-circle'"></i>
                </span>
              </p>
            </div>
          </div>
        </div>

        <!-- Fixes Applied Section -->
        <div v-if="qcStatus.results && qcStatus.results.fixesApplied && qcStatus.results.fixesApplied.length > 0" class="fixes-applied mt-4">
          <h5 class="subtitle is-6 mb-2">
            <span class="icon has-text-success"><i class="mdi mdi-wrench"></i></span>
            Auto-Fixes Applied
          </h5>
          <div class="tags">
            <span v-for="(fix, index) in qcStatus.results.fixesApplied" :key="index" class="tag is-success is-light">
              <span class="icon is-small"><i class="mdi mdi-check"></i></span>
              <span>{{ fix.description || fix.type }}</span>
            </span>
          </div>
          <div class="fix-details mt-2" v-if="hasLoudnessFix">
            <p class="is-size-7 has-text-grey">
              <strong>Loudness:</strong> {{ getLoudnessFixBefore }} LUFS → {{ getLoudnessFixAfter }} LUFS ({{ qcStatus.results.loudnessStandard || 'EBU R128' }})
            </p>
          </div>
          <div class="fix-details" v-if="hasAudioConversionFix">
            <p class="is-size-7 has-text-grey">
              <strong>Audio:</strong> Mono → Stereo
            </p>
          </div>
        </div>

        <!-- Output File Info -->
        <div v-if="qcStatus.results && qcStatus.results.outputPath" class="output-file mt-4">
          <h5 class="subtitle is-6 mb-2">
            <span class="icon has-text-info"><i class="mdi mdi-file-video"></i></span>
            Output File (MXF)
          </h5>
          <p class="is-size-7 has-text-grey">
            {{ qcStatus.results.outputPath }}
          </p>
        </div>

        <!-- Override Actions -->
        <div v-if="qcStatus.status === 'failed'" class="buttons mt-4">
          <b-button
            type="is-warning"
            icon-left="check-decagram"
            @click="showOverrideModal = true"
          >
            Override QC
          </b-button>
          <b-button
            type="is-danger"
            icon-left="refresh"
            @click="submitForTranscode"
            :loading="submittingJob"
          >
            Re-run Processing
          </b-button>
        </div>

        <!-- Override info if already overridden -->
        <div v-if="qcStatus.status === 'overridden'" class="notification is-info is-light mt-4">
          <span class="icon"><i class="mdi mdi-check-decagram"></i></span>
          <span>
            QC overridden by <strong>{{ qcStatus.overriddenBy }}</strong>
            <span v-if="qcStatus.overrideReason">: "{{ qcStatus.overrideReason }}"</span>
          </span>
        </div>
      </div>
    </div>

    <!-- File Browser Modal -->
    <b-modal
      :active.sync="showFileBrowser"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'x', 'outside']"
      width="900px"
    >
      <template #default>
        <div class="modal-card" style="width: 900px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Browse Storage - Select Source File
            </p>
            <button type="button" class="delete" @click="showFileBrowser = false" />
          </header>
          <section class="modal-card-body">
            <!-- Breadcrumb -->
            <nav class="breadcrumb is-small" aria-label="breadcrumbs">
              <ul>
                <li :class="{ 'is-active': currentPath === '' }">
                  <a @click="browseTo('')">Root</a>
                </li>
                <li v-for="(part, index) in pathParts" :key="index" :class="{ 'is-active': index === pathParts.length - 1 }">
                  <a @click="browseTo(pathParts.slice(0, index + 1).join('/'))">{{ part }}</a>
                </li>
              </ul>
            </nav>

            <!-- Search -->
            <b-field>
              <b-input
                v-model="searchTerm"
                placeholder="Search files..."
                icon="magnify"
                @keyup.enter="searchFiles"
              />
              <p class="control">
                <b-button type="is-info" @click="searchFiles">Search</b-button>
              </p>
            </b-field>

            <!-- File List -->
            <div class="file-browser-list" style="max-height: 400px; overflow-y: auto;">
              <b-table
                :data="browseResults"
                :loading="browsing"
                :hoverable="true"
                :striped="true"
                :narrowed="true"
              >
                <template #default="props">
                  <b-table-column field="type" label="" width="40">
                    <span class="icon">
                      <i :class="props.row.type === 'folder' ? 'mdi mdi-folder' : 'mdi mdi-file-video'"></i>
                    </span>
                  </b-table-column>
                  <b-table-column field="name" label="Name">
                    <a
                      v-if="props.row.type === 'folder'"
                      @click="browseTo(props.row.path)"
                      class="has-text-link"
                    >
                      {{ props.row.name }}/
                    </a>
                    <span
                      v-else
                      :class="{ 'has-text-weight-bold': isVideoFile(props.row.name) }"
                    >
                      {{ props.row.name }}
                    </span>
                  </b-table-column>
                  <b-table-column field="size" label="Size" width="100">
                    {{ props.row.size ? formatFileSize(props.row.size) : '-' }}
                  </b-table-column>
                  <b-table-column label="" width="160">
                    <div class="buttons are-small">
                      <b-button
                        v-if="props.row.type === 'file' && isVideoFile(props.row.name)"
                        type="is-light"
                        size="is-small"
                        icon-left="play"
                        title="Preview video"
                        @click="previewFile(props.row)"
                      >
                        Preview
                      </b-button>
                      <b-button
                        v-if="props.row.type === 'file' && isVideoFile(props.row.name)"
                        type="is-success"
                        size="is-small"
                        icon-left="link"
                        @click="selectFile(props.row)"
                      >
                        Link
                      </b-button>
                    </div>
                  </b-table-column>
                </template>
              </b-table>
            </div>

            <!-- Pagination info and Load More button -->
            <div class="pagination-info mt-3">
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <p class="is-size-7 has-text-grey">
                      Showing {{ totalFoldersLoaded }} folders and {{ totalFilesLoaded }} files
                      <span v-if="isTruncated" class="has-text-warning">
                        (more available)
                      </span>
                    </p>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item" v-if="isTruncated">
                    <b-button
                      type="is-info"
                      size="is-small"
                      icon-left="arrow-down"
                      :loading="loadingMore"
                      @click="loadMoreResults"
                    >
                      Load More
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showFileBrowser = false">Cancel</b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- QC Override Modal -->
    <b-modal :active.sync="showOverrideModal" has-modal-card trap-focus>
      <template #default>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Override QC Failure</p>
            <button type="button" class="delete" @click="showOverrideModal = false" />
          </header>
          <section class="modal-card-body">
            <div class="notification is-warning">
              <p><strong>Warning:</strong> You are about to approve a file that failed quality control checks.</p>
              <p class="mt-2">This action will be logged for audit purposes. Please provide a detailed reason.</p>
            </div>
            <b-field label="Reason for Override *" message="Minimum 10 characters required">
              <b-input
                v-model="overrideReason"
                type="textarea"
                placeholder="Explain why this QC failure can be ignored"
                rows="3"
              />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showOverrideModal = false">Cancel</b-button>
            <b-button
              type="is-warning"
              icon-left="check-decagram"
              :disabled="overrideReason.length < 10"
              :loading="processingOverride"
              @click="submitOverride"
            >
              Confirm Override
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- Video Preview Modal -->
    <b-modal
      :active.sync="showVideoPreview"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'x', 'outside']"
      width="960px"
      @close="closeVideoPreview"
    >
      <template #default>
        <div class="modal-card" style="width: 960px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <span class="icon"><i class="mdi mdi-play-circle"></i></span>
              {{ previewFilename }}
            </p>
            <button type="button" class="delete" @click="closeVideoPreview" />
          </header>
          <section class="modal-card-body video-preview-body">
            <div v-if="loadingPreview" class="has-text-centered py-6">
              <b-loading :is-full-page="false" :active="true" />
              <p class="mt-4">Loading video preview...</p>
            </div>
            <div v-else-if="previewError" class="has-text-centered py-6">
              <span class="icon is-large has-text-danger">
                <i class="mdi mdi-alert-circle mdi-48px"></i>
              </span>
              <p class="has-text-danger">{{ previewError }}</p>
            </div>
            <div v-else-if="previewUrl" class="video-container">
              <video
                ref="videoPlayer"
                :src="previewUrl"
                controls
                class="video-player"
                @error="handleVideoError"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="closeVideoPreview">Close</b-button>
            <b-button
              v-if="previewFileData"
              type="is-success"
              icon-left="link"
              @click="selectFileFromPreview"
            >
              Link This File
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import storageBrowseApi from '@/api/storageBrowse'
import mediaApi from '@/api/media'
import transcodeJobsApi from '@/api/transcodeJobs'

export default {
  name: 'MediaSourceLinker',
  props: {
    mediaId: {
      type: [Number, String],
      required: true
    },
    sourceFilename: {
      type: String,
      default: null
    },
    storagePath: {
      type: String,
      default: null
    },
    mediaType: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      browsing: false,
      storageConfigs: [],
      selectedStorageConfigId: null,
      showFileBrowser: false,
      currentPath: '',
      searchTerm: '',
      browseResults: [],
      qcStatus: null,
      submittingJob: false,
      activeJob: null,
      jobPollTimer: null,
      // Workflow templates
      workflowTemplates: [],
      selectedWorkflowTemplateId: null,
      showOverrideModal: false,
      overrideReason: '',
      processingOverride: false,
      // Video preview state
      showVideoPreview: false,
      previewUrl: null,
      previewFilename: '',
      previewFileData: null,
      loadingPreview: false,
      previewError: null,
      // Pagination state for folder browser
      nextContinuationToken: null,
      isTruncated: false,
      loadingMore: false,
      totalFoldersLoaded: 0,
      totalFilesLoaded: 0
    }
  },
  computed: {
    linkedFile() {
      if (this.sourceFilename && this.storagePath) {
        return {
          filename: this.sourceFilename,
          storagePath: this.storagePath
        }
      }
      return null
    },
    canSubmitForProcessing() {
      // Can submit if file is linked and no job in progress
      if (!this.linkedFile) return false
      if (this.activeJob && !['completed', 'failed', 'cancelled'].includes(this.activeJob.status)) return false
      return true
    },
    hasLoudnessFix() {
      if (!this.qcStatus?.results?.fixesApplied) return false
      return this.qcStatus.results.fixesApplied.some(f => f.type === 'loudness_normalization')
    },
    hasAudioConversionFix() {
      if (!this.qcStatus?.results?.fixesApplied) return false
      return this.qcStatus.results.fixesApplied.some(f => f.type === 'mono_to_stereo')
    },
    getLoudnessFixBefore() {
      if (!this.hasLoudnessFix) return null
      const fix = this.qcStatus.results.fixesApplied.find(f => f.type === 'loudness_normalization')
      return fix?.before?.replace(' LUFS', '') || 'N/A'
    },
    getLoudnessFixAfter() {
      if (!this.hasLoudnessFix) return null
      const fix = this.qcStatus.results.fixesApplied.find(f => f.type === 'loudness_normalization')
      return fix?.after?.replace(' LUFS', '') || '-23'
    },
    pathParts() {
      if (!this.currentPath) return []
      return this.currentPath.split('/').filter(p => p)
    }
  },
  watch: {
    mediaId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadQcStatus()
        }
      }
    }
  },
  mounted() {
    this.loadStorageConfigs()
    this.loadWorkflowTemplates()
  },
  beforeDestroy() {
    this.stopJobPolling()
  },
  methods: {
    async loadStorageConfigs() {
      try {
        this.storageConfigs = await storageBrowseApi.getConfigs()
        if (this.storageConfigs.length > 0) {
          this.selectedStorageConfigId = this.storageConfigs[0].id
        }
      } catch (error) {
        console.error('Failed to load storage configs:', error)
      }
    },

    async loadQcStatus() {
      try {
        const status = await mediaApi.getQcStatus(this.mediaId)
        this.qcStatus = status
      } catch (error) {
        // QC status not available yet
        this.qcStatus = null
      }
    },

    onStorageChange() {
      // Storage changed
    },

    openFileBrowser() {
      this.currentPath = ''
      this.searchTerm = ''
      this.browseResults = []
      this.showFileBrowser = true
      this.browseStorage()
    },

    async browseTo(path) {
      this.currentPath = path.replace(/\/$/, '')
      await this.browseStorage()
    },

    async browseStorage(loadMore = false) {
      if (!this.selectedStorageConfigId) return

      if (loadMore) {
        this.loadingMore = true
      } else {
        this.browsing = true
        // Reset pagination state for new path
        this.nextContinuationToken = null
        this.isTruncated = false
        this.totalFoldersLoaded = 0
        this.totalFilesLoaded = 0
      }

      try {
        const result = await storageBrowseApi.browse(
          this.selectedStorageConfigId,
          this.currentPath,
          '',
          loadMore ? this.nextContinuationToken : null
        )

        if (loadMore) {
          // Append to existing results
          this.browseResults = [...this.browseResults, ...result.folders, ...result.files]
          this.totalFoldersLoaded += result.folders.length
          this.totalFilesLoaded += result.files.length
        } else {
          // Replace results
          this.browseResults = [...result.folders, ...result.files]
          this.totalFoldersLoaded = result.folders.length
          this.totalFilesLoaded = result.files.length
        }

        // Store pagination info
        this.isTruncated = result.isTruncated
        this.nextContinuationToken = result.nextContinuationToken
      } catch (error) {
        console.error('Failed to browse storage:', error)
        this.$buefy.toast.open({
          message: 'Failed to browse storage',
          type: 'is-danger'
        })
      } finally {
        this.browsing = false
        this.loadingMore = false
      }
    },

    async loadMoreResults() {
      if (!this.nextContinuationToken || this.loadingMore) return
      await this.browseStorage(true)
    },

    async searchFiles() {
      if (!this.searchTerm || !this.selectedStorageConfigId) return

      this.browsing = true
      try {
        const result = await storageBrowseApi.search(
          this.selectedStorageConfigId,
          this.searchTerm,
          this.currentPath
        )
        this.browseResults = result.results
      } catch (error) {
        console.error('Failed to search files:', error)
      } finally {
        this.browsing = false
      }
    },

    async selectFile(file) {
      try {
        // Get full storage path
        const storageConfig = this.storageConfigs.find(c => c.id === this.selectedStorageConfigId)
        const storagePath = `s3://${storageConfig.bucket_name}/${file.path}`

        // Update media with linked file
        await mediaApi.linkSourceFile(this.mediaId, {
          sourceFilename: file.name,
          storagePath: storagePath,
          storageConfigId: this.selectedStorageConfigId
        })

        this.$buefy.toast.open({
          message: `Linked ${file.name} to media`,
          type: 'is-success'
        })

        this.showFileBrowser = false

        // Emit event for parent to update
        this.$emit('file-linked', {
          sourceFilename: file.name,
          storagePath: storagePath
        })
      } catch (error) {
        console.error('Failed to link file:', error)
        this.$buefy.toast.open({
          message: 'Failed to link file',
          type: 'is-danger'
        })
      }
    },

    async unlinkFile() {
      this.$buefy.dialog.confirm({
        title: 'Unlink Source File',
        message: 'Are you sure you want to unlink this source file?',
        confirmText: 'Unlink',
        cancelText: 'Cancel',
        type: 'is-danger',
        onConfirm: async () => {
          try {
            await mediaApi.unlinkSourceFile(this.mediaId)

            this.$buefy.toast.open({
              message: 'Source file unlinked',
              type: 'is-info'
            })

            // Emit event for parent to update
            this.$emit('file-unlinked')
          } catch (error) {
            console.error('Failed to unlink file:', error)
            this.$buefy.toast.open({
              message: 'Failed to unlink file',
              type: 'is-danger'
            })
          }
        }
      })
    },

    /**
     * Load available workflow templates for the transcode pipeline
     */
    async loadWorkflowTemplates() {
      try {
        const templates = await transcodeJobsApi.getAvailableWorkflows()
        // Filter to workflows that support single file input
        this.workflowTemplates = (templates || []).filter(t =>
          t.supports_single_file_input
        )
        // Auto-select format_convert as default
        const formatConvert = this.workflowTemplates.find(t => t.template_code === 'format_convert')
        if (formatConvert) {
          this.selectedWorkflowTemplateId = formatConvert.id
        } else if (this.workflowTemplates.length > 0) {
          this.selectedWorkflowTemplateId = this.workflowTemplates[0].id
        }
      } catch (error) {
        console.error('Failed to load workflow templates:', error)
      }
    },

    /**
     * Submit for transcode via the format_convert pipeline
     * QC + EBU R128 loudness normalization + encode are all built into the transcode worker
     */
    async submitForTranscode() {
      this.$buefy.dialog.confirm({
        title: 'Submit for QC & Processing',
        message: `This will:
          <ol style="margin-left: 1.5em; margin-top: 0.5em;">
            <li>Run QC analysis (loudness, black frames, technical specs)</li>
            <li>Auto-fix audio (EBU R128 normalization, mono→stereo)</li>
            <li>Encode for playout</li>
          </ol>
          <p style="margin-top: 0.5em;">Continue?</p>`,
        confirmText: 'Submit',
        type: 'is-success',
        onConfirm: async () => {
          this.submittingJob = true
          try {
            // Get storage config from linked file path
            const storageConfig = this.storageConfigs.find(c =>
              this.linkedFile.storagePath.includes(c.bucket_name)
            )

            if (!storageConfig) {
              throw new Error('Could not determine storage configuration')
            }

            if (!this.selectedWorkflowTemplateId) {
              throw new Error('No workflow template available. Check org workflow access in Super Admin.')
            }

            // Extract S3 key from full storage path (remove s3://bucket/ prefix)
            const fullPath = this.linkedFile.storagePath
            const s3Key = fullPath.replace(/^s3:\/\/[^/]+\//, '')

            const result = await transcodeJobsApi.submitJob({
              workflowTemplateId: this.selectedWorkflowTemplateId,
              inputStorageConfigId: storageConfig.id,
              inputPath: s3Key,
              mediaId: this.mediaId
            })

            this.$buefy.toast.open({
              message: 'Transcode job submitted',
              type: 'is-success',
              duration: 5000
            })

            // Start polling job status
            this.activeJob = {
              uuid: result.jobUuid,
              status: 'queued',
              progress: 0
            }

            this.startJobPolling(result.jobUuid)
            this.$emit('job-submitted', result)
          } catch (error) {
            console.error('Failed to submit transcode job:', error)
            this.$buefy.toast.open({
              message: error?.response?.data?.error || error.message || 'Failed to submit transcode job',
              type: 'is-danger'
            })
          } finally {
            this.submittingJob = false
          }
        }
      })
    },

    /**
     * Start polling job status
     */
    startJobPolling(jobUuid) {
      if (this.jobPollTimer) {
        clearInterval(this.jobPollTimer)
      }

      this.jobPollTimer = setInterval(async () => {
        try {
          const result = await transcodeJobsApi.getJobStatus(jobUuid)

          if (!result) return

          // The transcode job status endpoint returns spot_processing_jobs data
          const job = result.job || result
          const status = job.status || 'unknown'
          const progress = job.progress_percent || job.progress || 0

          this.activeJob = {
            uuid: jobUuid,
            status: status,
            progress: progress,
            statusMessage: job.status_message || job.statusMessage || '',
            errorMessage: job.error_message || job.errorMessage || ''
          }

          // Update QC status from job result if available
          if (job.qc_results || result.qc) {
            const qc = job.qc_results || result.qc
            this.qcStatus = {
              status: qc.passed ? 'passed' : 'failed',
              results: {
                errorCount: qc.issues?.filter(i => i.severity === 'error').length || 0,
                warningCount: qc.issues?.filter(i => i.severity === 'warning').length || 0,
                integratedLoudness: qc.loudness?.integrated || qc.integratedLoudness,
                truePeak: qc.loudness?.truePeak || qc.truePeak,
                loudnessCompliant: qc.loudness?.compliant || qc.loudnessCompliant,
                avSyncOk: qc.avSyncOk !== false,
                fixesApplied: qc.fixesApplied || [],
                loudnessStandard: qc.loudnessStandard,
                outputPath: qc.outputPath || job.output_path
              }
            }
          }

          // Stop polling if job is complete
          if (['completed', 'failed', 'cancelled', 'timeout'].includes(status)) {
            this.stopJobPolling()

            if (status === 'completed') {
              this.$buefy.toast.open({
                message: 'Processing completed successfully',
                type: 'is-success',
                duration: 5000
              })
              this.$emit('job-completed', result)
            } else if (status === 'failed') {
              this.$buefy.toast.open({
                message: this.activeJob.errorMessage || 'Processing failed',
                type: 'is-danger',
                duration: 8000
              })
              this.$emit('job-failed', result)
            }

            // Reload QC status from DB
            await this.loadQcStatus()
          }
        } catch (error) {
          console.error('Error polling job status:', error)
        }
      }, 5000) // Poll every 5 seconds (spot jobs are slower than legacy tx ingest)
    },

    /**
     * Stop job polling
     */
    stopJobPolling() {
      if (this.jobPollTimer) {
        clearInterval(this.jobPollTimer)
        this.jobPollTimer = null
      }
    },

    /**
     * Get notification class for job status
     */
    getJobNotificationClass(status) {
      switch (status) {
        case 'completed': return 'is-success is-light'
        case 'failed': return 'is-danger is-light'
        case 'cancelled': return 'is-warning is-light'
        case 'qc_failed': return 'is-danger is-light'
        default: return 'is-info is-light'
      }
    },

    /**
     * Get icon for job status
     */
    getJobStatusIcon(status) {
      switch (status) {
        case 'pending': return 'mdi mdi-clock-outline'
        case 'queued': return 'mdi mdi-playlist-play'
        case 'downloading': return 'mdi mdi-download'
        case 'qc_analyzing': return 'mdi mdi-magnify'
        case 'qc_passed': return 'mdi mdi-check-circle'
        case 'qc_failed': return 'mdi mdi-close-circle'
        case 'normalizing': return 'mdi mdi-volume-high'
        case 'encoding': return 'mdi mdi-movie-roll'
        case 'uploading': return 'mdi mdi-upload'
        case 'completed': return 'mdi mdi-check-circle'
        case 'failed': return 'mdi mdi-alert-circle'
        case 'cancelled': return 'mdi mdi-cancel'
        default: return 'mdi mdi-cog-sync'
      }
    },

    /**
     * Get label for job status
     */
    getJobStatusLabel(status) {
      switch (status) {
        case 'pending': return 'Pending'
        case 'queued': return 'Queued'
        case 'downloading': return 'Downloading...'
        case 'qc_analyzing': return 'Running QC Analysis...'
        case 'qc_passed': return 'QC Passed'
        case 'qc_failed': return 'QC Failed'
        case 'normalizing': return 'Normalizing Audio...'
        case 'encoding': return 'Encoding to MXF...'
        case 'uploading': return 'Uploading...'
        case 'completed': return 'Completed'
        case 'failed': return 'Failed'
        case 'cancelled': return 'Cancelled'
        default: return 'Processing...'
      }
    },

    /**
     * Get progress bar class for status
     */
    getProgressClass(status) {
      switch (status) {
        case 'completed': return 'is-success'
        case 'failed': return 'is-danger'
        case 'qc_failed': return 'is-danger'
        default: return 'is-info'
      }
    },

    async submitOverride() {
      this.processingOverride = true
      try {
        await mediaApi.overrideQc(this.mediaId, this.overrideReason)

        this.$buefy.toast.open({
          message: 'QC override approved',
          type: 'is-success'
        })

        this.showOverrideModal = false
        this.overrideReason = ''
        await this.loadQcStatus()

        this.$emit('qc-overridden')
      } catch (error) {
        console.error('Failed to override QC:', error)
        this.$buefy.toast.open({
          message: error?.response?.data?.error || 'Failed to override QC',
          type: 'is-danger'
        })
      } finally {
        this.processingOverride = false
      }
    },

    openQcReport() {
      if (this.qcStatus?.reportUrl) {
        window.open(this.qcStatus.reportUrl, '_blank')
      }
    },

    getQcStatusType(status) {
      switch (status) {
        case 'passed': return 'is-success'
        case 'passed_with_warnings': return 'is-warning'
        case 'overridden': return 'is-info'
        case 'failed': return 'is-danger'
        case 'processing': return 'is-light'
        case 'pending': return 'is-light'
        default: return 'is-light'
      }
    },

    getQcStatusLabel(status) {
      switch (status) {
        case 'passed': return 'Passed'
        case 'passed_with_warnings': return 'Passed with Warnings'
        case 'overridden': return 'Overridden'
        case 'failed': return 'Failed'
        case 'processing': return 'Processing'
        case 'pending': return 'Pending'
        default: return status || 'Unknown'
      }
    },

    /**
     * Get CSS class for true peak value
     * Green if <= -3 dBTP (EBU R128 compliant)
     * Red if > -3 dBTP (exceeds limit)
     */
    getTruePeakClass(truePeak) {
      if (truePeak === undefined || truePeak === null) return ''
      // EBU R128 max true peak is -3 dBTP
      return truePeak <= -3 ? 'has-text-success' : 'has-text-danger'
    },

    formatFileSize(bytes) {
      if (!bytes) return '-'
      const units = ['B', 'KB', 'MB', 'GB']
      let unitIndex = 0
      let size = bytes
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      return `${size.toFixed(1)} ${units[unitIndex]}`
    },

    isVideoFile(filename) {
      const ext = filename.split('.').pop().toLowerCase()
      return ['ts', 'mxf', 'mp4', 'mov', 'avi', 'mkv', 'm4v', 'mpg', 'mpeg'].includes(ext)
    },

    /**
     * Preview a video file
     */
    async previewFile(file) {
      this.previewFilename = file.name
      this.previewFileData = file
      this.previewUrl = null
      this.previewError = null
      this.loadingPreview = true
      this.showVideoPreview = true

      try {
        // Get presigned URL for preview. Historical note: this used to call
        // storageBrowseApi.getStreamUrl() — a method that has never existed
        // in the API client. The bug shipped silently because the catch
        // below masked the TypeError as "Failed to load video preview".
        const result = await storageBrowseApi.getPresignedUrl(
          this.selectedStorageConfigId,
          file.path
        )
        this.previewUrl = result.url
      } catch (error) {
        console.error('Failed to get preview URL:', error)
        this.previewError = error?.response?.data?.error || 'Failed to load video preview'
      } finally {
        this.loadingPreview = false
      }
    },

    /**
     * Close video preview and cleanup
     */
    closeVideoPreview() {
      // Pause video if playing
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        this.$refs.videoPlayer.src = ''
      }
      this.showVideoPreview = false
      this.previewUrl = null
      this.previewFilename = ''
      this.previewFileData = null
      this.previewError = null
    },

    /**
     * Handle video playback error
     */
    handleVideoError(event) {
      console.error('Video playback error:', event)
      this.previewError = 'Failed to play video. The format may not be supported by your browser.'
    },

    /**
     * Select file from preview modal
     */
    selectFileFromPreview() {
      if (this.previewFileData) {
        this.selectFile(this.previewFileData)
        this.closeVideoPreview()
      }
    }
  }
}
</script>

<style scoped>
.media-source-linker {
  margin-top: 1rem;
}

.media-source-linker .box {
  background: #ffffff;
  border: 1px solid #dbdbdb;
}

.mb-0 {
  margin-bottom: 0 !important;
}

.mt-4 {
  margin-top: 1rem;
}

.mt-3 {
  margin-top: 0.75rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.modal-card-body {
  color: #363636;
}

.file-browser-list {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.qc-status-panel .box {
  background: #ffffff;
  border: 1px solid #dbdbdb;
}

.job-progress .notification {
  padding: 0.75rem 1rem;
}

.job-progress .progress {
  margin-bottom: 0;
}

.fixes-applied .tags {
  margin-bottom: 0;
}

.fixes-applied .tag {
  margin-bottom: 0.25rem;
}

.fix-details {
  margin-top: 0.25rem;
}

.output-file {
  padding: 0.75rem;
  background: #f5f5f5;
  border-radius: 4px;
}

/* Video preview styles */
.video-preview-body {
  background-color: #000;
  padding: 0;
}

.video-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
}

.video-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
}

.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}
</style>
